import { useState, useCallback, useEffect, useRef } from 'react'
import { PanelLeft, Plus, Pencil } from 'lucide-react'
import { FChat } from './FChat'
import { FDocumentPanel } from './FDocumentPanel'
import { FSessionSidebar } from './FSessionSidebar'
import { FRenameSessionModal } from './FRenameSessionModal'
import { FMobileNav } from './FMobileNav'
import { ResizableDivider } from '@/features/develop/components/ResizableDivider'
import { useFStore } from '@/stores/useFStore'
import { useAuthStore } from '@/stores/useAuthStore'
import { useIsMobile } from '@/hooks/useMediaQuery'
import { cn } from '@/lib/utils'

const MIN_PANEL_WIDTH = 300
const MAX_PANEL_WIDTH = 800
const DEFAULT_PANEL_WIDTH = 500
const DEFAULT_PANEL_WIDTH_WITH_SIDEBAR = 400
const SIDEBAR_WIDTH = 260

const F_USER_ID_KEY = 'f-store-user-id'

export function FView() {
  const {
    sessionName,
    mobileView,
    showSessionSidebar,
    toggleSessionSidebar,
    closeSessionSidebar,
    createNewSession,
    loadSessionFromDb,
    renameSession,
    setMobileView,
    startNewSession,
  } = useFStore()

  const isMobile = useIsMobile()
  const [showRenameModal, setShowRenameModal] = useState(false)
  const [isVerifyingUser, setIsVerifyingUser] = useState(true)

  const { initialized: authInitialized, session: authSession, initialize: initializeAuth } = useAuthStore()
  const hasInitialized = useRef(false)

  // Initialize auth on mount
  useEffect(() => {
    initializeAuth()
  }, [initializeAuth])

  // Verify user ownership before showing content
  useEffect(() => {
    if (!authInitialized) return
    if (!authSession?.user?.id) {
      setIsVerifyingUser(false)
      return
    }

    const currentUserId = authSession.user.id
    const storedUserId = localStorage.getItem(F_USER_ID_KEY)

    console.log('[FView] Verifying user. Current:', currentUserId, 'Stored:', storedUserId)

    if (storedUserId && storedUserId !== currentUserId) {
      console.log('[FView] User mismatch! Clearing stale data')
      localStorage.setItem(F_USER_ID_KEY, currentUserId)
      localStorage.removeItem('f-store')
      startNewSession()
      hasInitialized.current = false
    } else if (!storedUserId) {
      console.log('[FView] First time for user, storing ID')
      localStorage.setItem(F_USER_ID_KEY, currentUserId)
    }

    setIsVerifyingUser(false)
  }, [authInitialized, authSession?.user?.id, startNewSession])

  // On mount: reload session from DB or create new
  useEffect(() => {
    if (hasInitialized.current) return
    if (!authInitialized || isVerifyingUser) return

    if (!authSession) {
      console.log('[FView] No auth session, redirecting to login')
      window.location.href = '/z/login?returnTo=' + encodeURIComponent(window.location.pathname)
      return
    }

    const checkAndInit = async () => {
      await new Promise(resolve => setTimeout(resolve, 50))

      const state = useFStore.getState()
      const hydratedSessionId = state.sessionId

      if (hasInitialized.current) return
      hasInitialized.current = true

      if (hydratedSessionId) {
        console.log('[FView] Loading session from DB:', hydratedSessionId)
        loadSessionFromDb(hydratedSessionId)
      } else {
        console.log('[FView] No session found, creating new')
        createNewSession()
      }
    }

    checkAndInit()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authInitialized, authSession, isVerifyingUser])

  const [documentWidth, setDocumentWidth] = useState(DEFAULT_PANEL_WIDTH)

  useEffect(() => {
    setDocumentWidth(showSessionSidebar ? DEFAULT_PANEL_WIDTH_WITH_SIDEBAR : DEFAULT_PANEL_WIDTH)
  }, [showSessionSidebar])

  const handleResize = useCallback((delta: number) => {
    setDocumentWidth(prev => {
      const newWidth = prev - delta
      return Math.min(MAX_PANEL_WIDTH, Math.max(MIN_PANEL_WIDTH, newWidth))
    })
  }, [])

  const handleMobileSessionSelect = useCallback(() => {
    setMobileView('chat')
  }, [setMobileView])

  // Block rendering until user verification completes
  if (isVerifyingUser || !authInitialized) {
    return (
      <div className="h-dvh flex items-center justify-center bg-background">
        <div className="text-muted-foreground text-sm">Loading...</div>
      </div>
    )
  }

  // Mobile layout
  if (isMobile) {
    return (
      <div className="h-dvh flex flex-col bg-background overflow-hidden">
        {/* Mobile Header */}
        <header className="flex-shrink-0 h-14 border-b border-border">
          <div className="px-4 h-full flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <h1 className="text-sm font-medium text-foreground truncate">{sessionName}</h1>
              <button
                onClick={() => setShowRenameModal(true)}
                className="p-1.5 text-muted-foreground hover:text-foreground rounded transition-colors flex-shrink-0"
                title="Rename session"
              >
                <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} />
              </button>
            </div>
            <button
              onClick={() => createNewSession()}
              className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded transition-colors"
              title="New session"
            >
              <Plus className="w-5 h-5" strokeWidth={1.5} />
            </button>
          </div>
        </header>

        <main className="flex-1 overflow-hidden pb-14 pb-mobile-nav">
          {mobileView === 'sessions' && (
            <div className="h-full">
              <FSessionSidebar
                onClose={() => setMobileView('chat')}
                onSessionSelect={handleMobileSessionSelect}
              />
            </div>
          )}
          {mobileView === 'chat' && (
            <div className="h-full">
              <FChat />
            </div>
          )}
          {mobileView === 'document' && (
            <div className="h-full">
              <FDocumentPanel />
            </div>
          )}
        </main>

        <FMobileNav />

        <FRenameSessionModal
          isOpen={showRenameModal}
          currentName={sessionName}
          onClose={() => setShowRenameModal(false)}
          onSubmit={(name) => {
            renameSession(name)
            setShowRenameModal(false)
          }}
        />
      </div>
    )
  }

  // Desktop layout
  return (
    <div className="h-dvh flex flex-col bg-background overflow-hidden">
      {/* Header */}
      <header className="flex-shrink-0 h-14">
        <div className="max-w-7xl mx-auto px-6 md:px-8 h-full flex items-center justify-between">
          <div className="flex items-center gap-1">
            <button
              onClick={toggleSessionSidebar}
              className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded transition-colors"
              title={showSessionSidebar ? "Hide sessions" : "Show sessions"}
            >
              <PanelLeft className="w-4 h-4" strokeWidth={1.5} />
            </button>
            <button
              onClick={() => createNewSession()}
              className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded transition-colors"
              title="New session"
            >
              <Plus className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </header>

      {/* Main content area */}
      <main className="flex-1 overflow-hidden">
        <div className="h-full max-w-7xl mx-auto px-6 md:px-8 flex">
          {/* Session Sidebar */}
          {showSessionSidebar && (
            <div
              className="flex-shrink-0 h-full border-r border-border"
              style={{ width: SIDEBAR_WIDTH }}
            >
              <FSessionSidebar onClose={closeSessionSidebar} />
            </div>
          )}

          {/* Main content column */}
          <div className={cn("flex-1 flex flex-col min-w-0", showSessionSidebar && "pl-4")}>
            {/* Session title */}
            <div className="flex-shrink-0 pt-3 pb-2 flex items-center gap-2 group">
              <h1 className="text-sm font-medium text-foreground">{sessionName}</h1>
              <button
                onClick={() => setShowRenameModal(true)}
                className="p-1 text-muted-foreground/0 group-hover:text-muted-foreground hover:!text-foreground rounded transition-colors"
                title="Rename session"
              >
                <Pencil className="w-3 h-3" strokeWidth={1.5} />
              </button>
            </div>

            {/* Chat pane */}
            <div className="flex-1 overflow-hidden">
              <FChat />
            </div>
          </div>

          <ResizableDivider onResize={handleResize} />

          {/* Document pane */}
          <div
            className="flex-shrink-0 overflow-hidden"
            style={{ width: documentWidth }}
          >
            <FDocumentPanel />
          </div>
        </div>
      </main>

      {/* Rename Session Modal */}
      <FRenameSessionModal
        isOpen={showRenameModal}
        currentName={sessionName}
        onClose={() => setShowRenameModal(false)}
        onSubmit={(name) => {
          renameSession(name)
          setShowRenameModal(false)
        }}
      />
    </div>
  )
}
